import { supabase } from "../lib/supabase";
import edgeFunctionService from "./edgeFunctionService";

/** Private Supabase Storage bucket for project context uploads. */
const PROJECT_CONTEXT_BUCKET = "project-context";
/** File size limit for most project context uploads. */
const PROJECT_CONTEXT_STANDARD_LIMIT_BYTES = 10 * 1024 * 1024;
/** File size limit for PowerPoint context uploads. */
const PROJECT_CONTEXT_PPTX_LIMIT_BYTES = 15 * 1024 * 1024;

/**
 * Format a byte limit as an MB label.
 * @param {number} bytes - Byte count.
 * @returns {string} Human-readable MB label.
 */
function formatLimit(bytes) {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

/**
 * Infer a normalized project context source type from a file.
 * @param {File} file - Browser File object.
 * @returns {string} Source type used by project_context_files.
 */
export function inferProjectContextSourceType(file) {
  const name = (file?.name || "").toLowerCase();
  const mime = (file?.type || "").toLowerCase();
  if (mime.startsWith("image/")) return "image";
  if (name.endsWith(".xlsx") || name.endsWith(".xlsm") || name.endsWith(".xls")) return "excel";
  if (name.endsWith(".csv") || mime.includes("csv")) return "csv";
  if (name.endsWith(".md") || mime.includes("markdown")) return "markdown";
  if (name.endsWith(".docx") || name.endsWith(".doc")) return "docx";
  if (name.endsWith(".pdf") || mime.includes("pdf")) return "pdf";
  if (name.endsWith(".pptx") || name.endsWith(".ppt")) return "pptx";
  if (mime.startsWith("text/") || name.endsWith(".txt")) return "text";
  return "unknown";
}

/**
 * Return the size limit for a project context file type.
 * @param {string} sourceType - Normalized source type.
 * @returns {number} File size limit in bytes.
 */
export function getProjectContextFileSizeLimit(sourceType) {
  return sourceType === "pptx"
    ? PROJECT_CONTEXT_PPTX_LIMIT_BYTES
    : PROJECT_CONTEXT_STANDARD_LIMIT_BYTES;
}

/**
 * Validate a project context file before upload.
 * @param {File} file - Browser File object.
 * @param {string} sourceType - Normalized source type.
 * @returns {void}
 */
export function validateProjectContextFile(file, sourceType) {
  const limit = getProjectContextFileSizeLimit(sourceType);
  if ((file?.size || 0) > limit) {
    const label = sourceType === "pptx" ? "PowerPoint files" : "Project context files";
    throw new Error(`${label} must be ${formatLimit(limit)} or smaller.`);
  }
}

/**
 * Build a collision-resistant private object path.
 * @param {object} params - Path inputs.
 * @param {string} params.userId - Auth user id.
 * @param {string} params.projectId - Project id.
 * @param {string} params.fileName - Original file name.
 * @returns {string} Storage path under the authenticated user's folder.
 */
function buildStoragePath({ userId, projectId, fileName }) {
  const safeName = String(fileName || "context-file")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120) || "context-file";
  const random =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${userId}/${projectId}/${random}-${safeName}`;
}

/**
 * Upload a project context file and queue asynchronous ingestion.
 * @param {object} params - Upload params.
 * @param {File} params.file - File to upload.
 * @param {string} params.projectId - Project id.
 * @param {string} params.accountId - Account id.
 * @param {string} params.requestId - ai_requests row id.
 * @returns {Promise<object>} Created context file row.
 */
export async function uploadProjectContextFile({
  file,
  projectId,
  accountId,
  requestId,
}) {
  if (!file) throw new Error("file is required");
  if (!projectId) throw new Error("projectId is required");
  if (!accountId) throw new Error("accountId is required");
  if (!requestId) throw new Error("requestId is required");
  const sourceType = inferProjectContextSourceType(file);
  validateProjectContextFile(file, sourceType);

  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw userError;
  if (!user) throw new Error("Not authenticated");

  const storagePath = buildStoragePath({
    userId: user.id,
    projectId,
    fileName: file.name,
  });

  const { error: uploadError } = await supabase.storage
    .from(PROJECT_CONTEXT_BUCKET)
    .upload(storagePath, file, {
      contentType: file.type || "application/octet-stream",
      upsert: false,
    });
  if (uploadError) throw uploadError;

  const { data: contextFile, error: insertError } = await supabase
    .from("project_context_files")
    .insert({
      account_id: accountId,
      project_id: projectId,
      uploaded_by: user.id,
      storage_bucket: PROJECT_CONTEXT_BUCKET,
      storage_path: storagePath,
      name: file.name,
      mime_type: file.type || "application/octet-stream",
      size_bytes: file.size || 0,
      source_type: sourceType,
      status: "pending",
      metadata: {
        lastModified: file.lastModified || null,
      },
    })
    .select()
    .single();

  if (insertError) {
    await supabase.storage.from(PROJECT_CONTEXT_BUCKET).remove([storagePath]);
    throw insertError;
  }

  await edgeFunctionService.ingestProjectContext({
    requestId,
    fileId: contextFile.id,
    projectId,
    accountId,
  });

  return contextFile;
}

/**
 * Fetch project context file metadata for display.
 * @param {string} projectId - Project id.
 * @returns {Promise<Array<object>>} Context file rows.
 */
export async function listProjectContextFiles(projectId) {
  if (!projectId) return [];
  const { data, error } = await supabase
    .from("project_context_files")
    .select("*")
    .eq("project_id", projectId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

/**
 * Create a short-lived signed URL for viewing an uploaded project context file.
 * @param {object} file - project_context_files row.
 * @returns {Promise<string>} Signed URL for the private Storage object.
 */
export async function createProjectContextFileViewUrl(file) {
  if (!file?.storage_bucket || !file?.storage_path) {
    throw new Error("Storage path is missing for this file.");
  }
  const { data, error } = await supabase.storage
    .from(file.storage_bucket)
    .createSignedUrl(file.storage_path, 5 * 60);
  if (error) throw error;
  if (!data?.signedUrl) throw new Error("Could not create a file view link.");
  return data.signedUrl;
}

/**
 * Delete a project context file, its Storage object, and cascaded chunks.
 * @param {object} file - project_context_files row.
 * @returns {Promise<void>}
 */
export async function deleteProjectContextFile(file) {
  if (!file?.id) throw new Error("file id is required");
  if (file.storage_bucket && file.storage_path) {
    const { error: storageError } = await supabase.storage
      .from(file.storage_bucket)
      .remove([file.storage_path]);
    if (storageError) throw storageError;
  }

  const { error: deleteError } = await supabase
    .from("project_context_files")
    .delete()
    .eq("id", file.id);
  if (deleteError) throw deleteError;
}

const projectContextService = {
  inferProjectContextSourceType,
  getProjectContextFileSizeLimit,
  validateProjectContextFile,
  uploadProjectContextFile,
  listProjectContextFiles,
  createProjectContextFileViewUrl,
  deleteProjectContextFile,
};

export default projectContextService;
